<script>
    import { onMount, createEventDispatcher } from 'svelte';
    import { t } from 'svelte-i18n';
    import Swal from 'sweetalert2';
    import { toasts } from "svelte-toasts";

    // Props
    export let parentEntityId = null;
    export let parentEntity = null;
    export let saveParentEntity = null;
    export let collection = '';
    export let relationField = '';
    export let columns = [];
    export let editColumns = [];
    export let fetchDetails = null;
    export let saveDetail = null;
    export let removeDetail = null;
    export let searchDetails = null;
    export let subscribeToDetails = null;
    export let unsubscribeFromDetails = null;

    // State
    let details = [];
    let totalItems = 0;
    let currentPage = 1;
    let perPage = 10;
    let searchQuery = '';
    let isLoading = false;
    let isDetailPaneOpen = false;
    let currentDetail = null;
    let currentDetailId = null;
    let newDetail = {};
    
    // Autocomplete state
    let productOptions = [];
    let productSearchQuery = '';
    let showProductDropdown = false;
    let filteredProducts = [];
    let selectedProductDescrip = '';

    const dispatch = createEventDispatcher();

    // Initialize new detail object
    $: if (parentEntityId && relationField) {
        newDetail = {
            [relationField]: parentEntityId,
            ...Object.fromEntries(editColumns.map(col => [col.key, col.type === 'number' ? 0 : '']))
        };
    }

    // Load details
    async function loadDetails(page = 1) {
        if (!parentEntityId || !fetchDetails) {
            // If no parent entity ID, just show empty state
            details = [];
            totalItems = 0;
            currentPage = 1;
            return;
        }
        
        isLoading = true;
        try {
            const response = await fetchDetails(parentEntityId, page, perPage);
            if (response) {
                details = response[collection] || [];
                totalItems = response.totalItems || 0;
                currentPage = response.page || 1;
            }
        } catch (error) {
            console.error('Error loading details:', error);
            toasts.add({
                title: 'Error',
                description: 'Error loading details',
                type: 'error',
                duration: 3000,
            });
        } finally {
            isLoading = false;
        }
    }

    // Search details
    async function handleSearch(page = 1) {
        if (!parentEntityId || !searchDetails) {
            // If no parent entity ID, just show empty state
            details = [];
            totalItems = 0;
            currentPage = 1;
            return;
        }
        
        isLoading = true;
        try {
            const response = searchQuery.trim() === ''
                ? await fetchDetails(parentEntityId, page, perPage)
                : await searchDetails(parentEntityId, searchQuery, page, perPage);
            
            if (response) {
                details = response[collection] || [];
                totalItems = response.totalItems || 0;
                currentPage = response.page || 1;
            }
        } catch (error) {
            console.error('Error searching details:', error);
        } finally {
            isLoading = false;
        }
    }

    // Save detail
    async function saveDetailEntry() {
        if (!saveDetail) return;
        
        try {
            // Store the current detail data before any async operations
            const detailDataToSave = { ...newDetail };
            console.log('Stored detail data before parent save:', detailDataToSave);
            
            // If parent entity doesn't exist, save it first
            if (!parentEntityId && parentEntity && saveParentEntity) {
                // Validate required fields for parent entity
                const requiredFields = ['numero', 'fecha', 'nit', 'nombre'];
                const missingFields = requiredFields.filter(field => !parentEntity[field] || parentEntity[field].toString().trim() === '');
                
                if (missingFields.length > 0) {
                    toasts.add({
                        title: 'Error',
                        description: `Please fill in the required fields first: ${missingFields.join(', ')}`,
                        type: 'error',
                        duration: 5000,
                    });
                    return;
                }
                
                // Ensure numero is generated if it's missing
                if (!parentEntity.numero || parentEntity.numero.trim() === '') {
                    try {
                        // Import and call the generateCotizaNumber function
                        const { generateCotizaNumber } = await import('../controllers/cotizaCabController.js');
                        const numero = await generateCotizaNumber();
                        parentEntity.numero = numero;
                        console.log('Generated numero for parent entity:', numero);
                    } catch (error) {
                        console.error('Error generating numero:', error);
                        toasts.add({
                            title: 'Error',
                            description: 'Error generating quotation number',
                            type: 'error',
                            duration: 3000,
                        });
                        return;
                    }
                }
                
                toasts.add({
                    title: 'Info',
                    description: 'Saving main record first...',
                    type: 'info',
                    duration: 2000,
                });
                
                console.log('Saving parent entity with data:', parentEntity);
                
                // Save the parent entity first (without closing the pane)
                const result = await saveParentEntity();
                console.log('Parent save result:', result);
                
                // Update parentEntityId with the new ID
                if (result && result.id) {
                    parentEntityId = result.id;
                    // Update the stored detail data with the new parent ID
                    detailDataToSave[relationField] = parentEntityId;
                    console.log('Parent entity saved with ID:', parentEntityId);
                    console.log('Updated detail data with parent ID:', detailDataToSave);
                } else {
                    console.error('Invalid result from parent save:', result);
                    throw new Error('Failed to get parent entity ID after saving');
                }
            } else if (parentEntityId) {
                // If parent already exists, just add the parent ID to the detail data
                detailDataToSave[relationField] = parentEntityId;
            }
            
            // Now save the detail with the preserved data
            console.log('Saving detail with preserved data:', detailDataToSave);
            await saveDetail(detailDataToSave, currentDetailId);
            await loadDetails(currentPage);
            
            // Reset the form for adding another item instead of closing the pane
            if (!currentDetailId) {
                // If this was a new item, reset the form for adding another
                newDetail = {};
                editColumns.forEach(col => {
                    if (col.key !== 'producto') { // producto is handled separately
                        newDetail[col.key] = col.type === 'number' ? 0 : '';
                    }
                });
                productSearchQuery = '';
                selectedProductDescrip = '';
                console.log('Form reset for adding another item');
            } else {
                // If this was editing an existing item, close the pane
            closeDetailPane();
            }
            
            dispatch('detailSaved');
            
            toasts.add({
                title: 'Success',
                description: 'Detail saved successfully',
                type: 'success',
                duration: 3000,
            });
        } catch (error) {
            console.error('Error saving detail:', error);
            toasts.add({
                title: 'Error',
                description: 'Error saving detail: ' + error.message,
                type: 'error',
                duration: 3000,
            });
        }
    }

    // Remove detail
    async function removeDetailEntry(id) {
        if (!removeDetail) return;
        
        try {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                text: "No podrás revertir esta acción",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await removeDetail(id);
                await loadDetails(currentPage);
                dispatch('detailRemoved');
            }
        } catch (error) {
            console.error('Error removing detail:', error);
        }
    }

    // Open detail pane for editing
    function openDetailPane(detail = null) {
        currentDetail = detail;
        currentDetailId = detail ? detail.id : null;
        
        if (detail) {
            // Copy detail data to newDetail for editing
            newDetail = { ...detail };
            
            // Set product search query and description for editing
            if (detail.expand?.producto) {
                const producto = detail.expand.producto;
                productSearchQuery = `${producto.sku || ''} - ${producto.nombre || ''}`;
                selectedProductDescrip = producto.descrip || '';
            }
        } else {
            // Reset to new detail - parentEntityId will be set when saving
            newDetail = {};
            editColumns.forEach(col => {
                if (col.key !== 'producto') { // producto is handled separately
                    newDetail[col.key] = col.type === 'number' ? 0 : '';
                }
            });
            productSearchQuery = '';
            selectedProductDescrip = '';
        }
        
        isDetailPaneOpen = true;
    }

    // Close detail pane
    function closeDetailPane() {
        isDetailPaneOpen = false;
        currentDetail = null;
        currentDetailId = null;
        newDetail = {};
        productSearchQuery = '';
        showProductDropdown = false;
        selectedProductDescrip = '';
    }

    // Handle click outside to close autocomplete
    function handleClickOutside(event) {
        if (!event.target.closest('.relative')) {
            showProductDropdown = false;
        }
    }

    // Handle input changes
    function handleInputChange(event) {
        const { name, value } = event.target;
        newDetail[name] = value;
        newDetail = { ...newDetail }; // Trigger reactivity
        
        // Auto-calculate total if it's a detail with cantidad and precio
        if (name === 'cantidad' || name === 'precio') {
            const cantidad = parseFloat(newDetail.cantidad) || 0;
            const precio = parseFloat(newDetail.precio) || 0;
            newDetail.total = cantidad * precio;
            newDetail = { ...newDetail }; // Trigger reactivity again
        }
    }

    // Handle select changes
    function handleSelectChange(event) {
        const { name, value } = event.target;
        newDetail[name] = value;
        newDetail = { ...newDetail }; // Trigger reactivity
    }

    // Handle autocomplete selection
    function handleProductSelect(product) {
        newDetail.producto = product.value;
        productSearchQuery = product.label;
        selectedProductDescrip = product.descrip;
        showProductDropdown = false;
        newDetail = { ...newDetail }; // Trigger reactivity
        console.log('Product selected, newDetail updated:', newDetail);
    }

    // Filter products based on search query
    $: {
        if (productSearchQuery && productOptions.length > 0) {
            filteredProducts = productOptions.filter(product => 
                product.label.toLowerCase().includes(productSearchQuery.toLowerCase())
            );
        } else {
            filteredProducts = productOptions;
        }
    }

    // Load product options when component mounts
    async function loadProductOptions() {
        try {
            // Import the fetch function dynamically
            const { fetchProductos } = await import('../controllers/productoController.js');
            const response = await fetchProductos(1, 1000);
            if (response && response.producto) {
                productOptions = response.producto.map(producto => ({
                    value: producto.id,
                    label: `${producto.sku || ''} - ${producto.nombre || ''}`,
                    sku: producto.sku || '',
                    nombre: producto.nombre || '',
                    descrip: producto.descrip || ''
                }));
                filteredProducts = productOptions;
            }
        } catch (error) {
            console.error('Error loading product options:', error);
        }
    }

    // Helper functions for dynamic field types
    function getSelectedAtributo() {
        if (!newDetail.atributo) return null;
        
        // Find the atributo in the options
        const atributoColumn = editColumns.find(col => col.key === 'atributo');
        if (!atributoColumn || !atributoColumn.options) {
            return null;
        }
        
        const selectedOption = atributoColumn.options.find(opt => opt.value === newDetail.atributo);
        return selectedOption ? selectedOption.atributo : null;
    }

    // Get current selected atributo and field type - make reactive to newDetail.atributo
    $: selectedAtributo = newDetail.atributo ? getSelectedAtributo() : null;
    $: valorFieldType = selectedAtributo ? selectedAtributo.tipo_dato : 'texto';
    $: valorFieldOptions = (() => {
        if (selectedAtributo && selectedAtributo.tipo_dato === 'lista' && selectedAtributo.lista_items) {
            // Parse the lista_items (assuming it's a comma-separated string)
            const items = selectedAtributo.lista_items.split(',').map(item => item.trim());
            return items.map(item => ({ value: item, label: item }));
        }
        return [];
    })();
    
    

    // Clear valor field when atributo changes
    let lastAtributo = null;
    $: if (newDetail.atributo && lastAtributo !== newDetail.atributo) {
        // Only clear if the atributo actually changed (not on initial load)
        if (lastAtributo !== null) {
            newDetail.valor = '';
            newDetail = { ...newDetail }; // Trigger reactivity
        }
        lastAtributo = newDetail.atributo;
    }

    function getDynamicFieldType(fieldKey) {
        if (fieldKey === 'valor') {
            return valorFieldType;
        }
        return 'texto';
    }

    function getDynamicFieldOptions(fieldKey) {
        if (fieldKey === 'valor') {
            return valorFieldOptions;
        }
        return [];
    }

    // Load details on mount and when parentEntityId changes
    $: if (parentEntityId) {
        loadDetails();
    }

    onMount(() => {
        
        if (subscribeToDetails) {
            subscribeToDetails(parentEntityId, searchQuery, handleSearch);
        }
        
        // Load product options for autocomplete
        loadProductOptions();
        
        return () => {
            if (unsubscribeFromDetails) {
                unsubscribeFromDetails();
            }
        };
    });
</script>

<div class="embedded-detail-table">
    <!-- Header with search and add button -->
    <div class="flex justify-between items-center mb-4">
        <div class="flex items-center space-x-4">
            <input
                type="text"
                placeholder="Search details..."
                bind:value={searchQuery}
                on:input={() => handleSearch()}
                class="px-3 py-2 border rounded-md text-sm"
            />
        </div>
        <button
            on:click={() => openDetailPane()}
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
        >
            + Add Item
        </button>
    </div>

    <!-- Details table -->
    <div class="overflow-x-auto">
        <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
                <tr>
                    {#each columns as column}
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {column.label}
                        </th>
                    {/each}
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                    </th>
                </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
                {#if isLoading}
                    <tr>
                        <td colspan={columns.length + 1} class="px-4 py-8 text-center text-gray-500">
                            Loading...
                        </td>
                    </tr>
                {:else if details.length === 0}
                    <tr>
                        <td colspan={columns.length + 1} class="px-4 py-8 text-center text-gray-500">
                            No items found
                        </td>
                    </tr>
                {:else}
                    {#each details as detail}
                        <tr class="hover:bg-gray-50">
                            {#each columns as column}
                                <td class="px-4 py-3 text-sm text-gray-900">
                                    {#if column.format}
                                        <span title={column.field === 'producto' && detail.expand?.producto?.descrip ? detail.expand.producto.descrip : ''}>
                                            {column.format(detail[column.field], detail)}
                                        </span>
                                    {:else if column.type === 'relation' && detail[column.field] && detail[column.field].expand}
                                        <span title={column.field === 'producto' && detail.expand?.producto?.descrip ? detail.expand.producto.descrip : ''}>
                                            {detail[column.field].expand[column.relationField] || '-'}
                                        </span>
                                    {:else if column.type === 'currency'}
                                        ${(detail[column.field] || 0).toFixed(2)}
                                    {:else if column.type === 'date'}
                                        {detail[column.field] ? new Date(detail[column.field]).toLocaleDateString() : '-'}
                                    {:else}
                                        {detail[column.field] || '-'}
                                    {/if}
                                </td>
                            {/each}
                            <td class="px-4 py-3 text-sm text-gray-500">
                                <button
                                    on:click={() => openDetailPane(detail)}
                                    class="text-blue-600 hover:text-blue-900 mr-2"
                                >
                                    Edit
                                </button>
                                <button
                                    on:click={() => removeDetailEntry(detail.id)}
                                    class="text-red-600 hover:text-red-900"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    {/each}
                {/if}
            </tbody>
        </table>
    </div>

    <!-- Pagination -->
    {#if totalItems > perPage}
        <div class="flex justify-between items-center mt-4">
            <div class="text-sm text-gray-700">
                Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} results
            </div>
            <div class="flex space-x-2">
                <button
                    on:click={() => handleSearch(currentPage - 1)}
                    disabled={currentPage <= 1}
                    class="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                <span class="px-3 py-1 bg-blue-600 text-white rounded">
                    {currentPage}
                </span>
                <button
                    on:click={() => handleSearch(currentPage + 1)}
                    disabled={currentPage * perPage >= totalItems}
                    class="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>
        </div>
    {/if}
</div>

<!-- Detail Edit Pane -->
{#if isDetailPaneOpen}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click={handleClickOutside}>
        <div class="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-semibold">
                    {currentDetailId ? 'Edit Item' : 'Add Item'}
                </h3>
                <button
                    on:click={closeDetailPane}
                    class="text-gray-400 hover:text-gray-600"
                >
                    ✕
                </button>
            </div>

            <form on:submit|preventDefault={saveDetailEntry}>
                {#each editColumns as column}
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            {column.label}
                        </label>
                        {#if column.key === 'producto'}
                            <!-- Autocomplete for products -->
                            <div class="relative">
                                <input
                                    type="text"
                                    bind:value={productSearchQuery}
                                    on:focus={() => showProductDropdown = true}
                                    on:input={() => showProductDropdown = true}
                                    placeholder="Type to search products..."
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    title={selectedProductDescrip}
                                />
                                {#if showProductDropdown && filteredProducts.length > 0}
                                    <div class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {#each filteredProducts as product}
                                            <div
                                                on:click={() => handleProductSelect(product)}
                                                class="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                title={product.descrip}
                                            >
                                                {product.label}
                                            </div>
                                        {/each}
                                    </div>
                                {/if}
                            </div>
                        {:else if column.type === 'number'}
                            <input
                                type="number"
                                name={column.key}
                                value={newDetail[column.key] || 0}
                                on:input={(e) => {
                                    newDetail[column.key] = parseFloat(e.target.value) || 0;
                                    // Auto-calculate total if it's a detail with cantidad and precio
                                    if (column.key === 'cantidad' || column.key === 'precio') {
                                        const cantidad = parseFloat(newDetail.cantidad) || 0;
                                        const precio = parseFloat(newDetail.precio) || 0;
                                        newDetail.total = cantidad * precio;
                                        newDetail = { ...newDetail }; // Trigger reactivity
                                    }
                                }}
                                required={column.required}
                                readonly={column.readonly}
                                step={column.step || 1}
                                min={column.min || 0}
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 {column.readonly ? 'bg-gray-100' : ''}"
                            />
                        {:else if column.dynamicType}
                            <!-- Dynamic field type based on selected atributo -->
                            {#if valorFieldType === 'booleano'}
                                <select
                                    name={column.key}
                                    value={newDetail[column.key] || ''}
                                    on:change={(e) => {
                                        newDetail[column.key] = e.target.value;
                                        newDetail = { ...newDetail }; // Trigger reactivity
                                    }}
                                    required={column.required}
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select...</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            {:else if valorFieldType === 'fecha'}
                                <input
                                    type="date"
                                    name={column.key}
                                    value={newDetail[column.key] || ''}
                                    on:input={(e) => {
                                        newDetail[column.key] = e.target.value;
                                        newDetail = { ...newDetail }; // Trigger reactivity
                                    }}
                                    required={column.required}
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            {:else if valorFieldType === 'lista'}
                                <select
                                    name={column.key}
                                    value={newDetail[column.key] || ''}
                                    on:change={(e) => {
                                        newDetail[column.key] = e.target.value;
                                        newDetail = { ...newDetail }; // Trigger reactivity
                                    }}
                                    required={column.required}
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select...</option>
                                    {#each valorFieldOptions as option}
                                        <option value={option.value}>{option.label}</option>
                                    {/each}
                                </select>
                            {:else if valorFieldType === 'numerico'}
                                <input
                                    type="number"
                                    name={column.key}
                                    value={newDetail[column.key] || ''}
                                    on:input={(e) => {
                                        newDetail[column.key] = e.target.value;
                                        newDetail = { ...newDetail }; // Trigger reactivity
                                    }}
                                    required={column.required}
                                    step="0.01"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            {:else}
                                <input
                                    type="text"
                                    name={column.key}
                                    value={newDetail[column.key] || ''}
                                    on:input={(e) => {
                                        newDetail[column.key] = e.target.value;
                                        newDetail = { ...newDetail }; // Trigger reactivity
                                    }}
                                    required={column.required}
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            {/if}
                        {:else if column.type === 'text'}
                            <input
                                type="text"
                                name={column.key}
                                value={newDetail[column.key] || ''}
                                on:input={(e) => {
                                    newDetail[column.key] = e.target.value;
                                    newDetail = { ...newDetail }; // Trigger reactivity
                                }}
                                required={column.required}
                                readonly={column.readonly}
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 {column.readonly ? 'bg-gray-100' : ''}"
                            />
                        {:else if column.type === 'select' && column.options}
                            <select
                                name={column.key}
                                value={newDetail[column.key] || ''}
                                on:change={(e) => {
                                    newDetail[column.key] = e.target.value;
                                    newDetail = { ...newDetail }; // Trigger reactivity
                                }}
                                required={column.required}
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select {column.label}...</option>
                                {#each column.options as option}
                                    <option value={option.value}>{option.label}</option>
                                {/each}
                            </select>
                        {:else if column.type === 'boolean'}
                            <select
                                name={column.key}
                                value={newDetail[column.key] || ''}
                                on:change={(e) => {
                                    newDetail[column.key] = e.target.value;
                                    newDetail = { ...newDetail }; // Trigger reactivity
                                }}
                                required={column.required}
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select...</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        {:else if column.type === 'date'}
                            <input
                                type="date"
                                name={column.key}
                                value={newDetail[column.key] || ''}
                                on:input={(e) => {
                                    newDetail[column.key] = e.target.value;
                                    newDetail = { ...newDetail }; // Trigger reactivity
                                }}
                                required={column.required}
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        {:else}
                            <input
                                type="text"
                                name={column.key}
                                value={newDetail[column.key] || ''}
                                on:input={(e) => {
                                    newDetail[column.key] = e.target.value;
                                    newDetail = { ...newDetail }; // Trigger reactivity
                                }}
                                required={column.required}
                                readonly={column.readonly}
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 {column.readonly ? 'bg-gray-100' : ''}"
                            />
                        {/if}
                    </div>
                {/each}

                <div class="flex justify-end space-x-3 mt-6">
                    <button
                        type="button"
                        on:click={closeDetailPane}
                        class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {currentDetailId ? 'Update' : 'Create'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}
